import React, { useState } from 'react';
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Support as SupportIcon,
  RoomService,
  Edit as EditIcon,
  Help as HelpIcon,
  Receipt as ReceiptIcon,
  NotificationImportant as BellIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import { customerRequestApi } from '../api/api';
import { RequestType, REQUEST_TYPE_LABELS } from '../types/notification.types';
import { useAuth } from '../context/AuthContext';

interface CustomerRequestButtonProps {
  vendorId: number;
  tableId: number;
  orderId?: number;
}

// Only show Bill Request and Call Steward options
const ALLOWED_REQUEST_TYPES = [RequestType.BILL_REQUEST, RequestType.CALL_STEWARD];

const REQUEST_TYPE_MUI_ICONS: Record<RequestType, React.ReactElement> = {
  [RequestType.TABLE_SERVICE]: <RoomService />,
  [RequestType.ORDER_MODIFICATION]: <EditIcon />,
  [RequestType.ASSISTANCE]: <HelpIcon />,
  [RequestType.BILL_REQUEST]: <ReceiptIcon />,
  [RequestType.CALL_STEWARD]: <BellIcon />,
  [RequestType.CUSTOM_MESSAGE]: <MessageIcon />
};

const REQUEST_TYPE_DESCRIPTIONS: Record<RequestType, string> = {
  [RequestType.TABLE_SERVICE]: 'Request table service (water, napkins, etc.)',
  [RequestType.ORDER_MODIFICATION]: 'Modify your current order',
  [RequestType.ASSISTANCE]: 'General assistance needed',
  [RequestType.BILL_REQUEST]: 'Request your bill',
  [RequestType.CALL_STEWARD]: 'Call a steward to your table',
  [RequestType.CUSTOM_MESSAGE]: 'Send a custom message'
};

export const CustomerRequestButton: React.FC<CustomerRequestButtonProps> = ({
  vendorId,
  tableId,
  orderId
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  
  const [open, setOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<RequestType | null>(null);
  const [message, setMessage] = useState('');
  const [customerName, setCustomerName] = useState((user as any)?.name || '');
  const [customerPhone, setCustomerPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSelectedType(null);
    setMessage('');
    setError(null);
  };

  const handleTypeSelect = (type: RequestType) => {
    setSelectedType(type);
    // Pre-fill message for common requests
    switch (type) {
      case RequestType.TABLE_SERVICE:
        setMessage('Please refill water');
        break;
      case RequestType.BILL_REQUEST:
        setMessage('Please bring the bill');
        break;
      case RequestType.CALL_STEWARD:
        setMessage('Please come to our table');
        break;
      default:
        setMessage('');
    }
  };

  const handleSubmit = async () => {
    if (!selectedType || !message.trim()) {
      setError('Please select a request type and enter a message');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await customerRequestApi.create({
        vendorId,
        tableId,
        orderId,
        requestType: selectedType,
        message: message.trim(),
        customerName: customerName.trim() || undefined,
        customerPhone: customerPhone.trim() || undefined
      });

      setSuccess(true);
      handleClose();
      
      // Show success message
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="request assistance"
        onClick={handleOpen}
        sx={{
          position: 'fixed',
          bottom: isMobile ? 80 : 24,
          right: 24,
          zIndex: 1200
        }}
      >
        <SupportIcon />
      </Fab>

      {/* Request Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {selectedType ? REQUEST_TYPE_LABELS[selectedType] : 'Request Assistance'}
        </DialogTitle>
        
        <DialogContent>
          {!selectedType ? (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                How can we help you today?
              </Typography>
              <List>
                {ALLOWED_REQUEST_TYPES.map((value) => (
                  <ListItem
                    key={value}
                    button
                    onClick={() => handleTypeSelect(value)}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover
                      }
                    }}
                  >
                    <ListItemIcon>
                      {REQUEST_TYPE_MUI_ICONS[value]}
                    </ListItemIcon>
                    <ListItemText
                      primary={REQUEST_TYPE_LABELS[value]}
                      secondary={REQUEST_TYPE_DESCRIPTIONS[value]}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <Box>
              <Button
                size="small"
                onClick={() => setSelectedType(null)}
                sx={{ mb: 2 }}
              >
                ← Back to options
              </Button>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                variant="outlined"
                margin="normal"
                required
                helperText="Describe what you need"
              />

              {!user && (
                <>
                  <TextField
                    fullWidth
                    label="Your Name (Optional)"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    variant="outlined"
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Phone Number (Optional)"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    variant="outlined"
                    margin="normal"
                    placeholder="+91 9876543210"
                  />
                </>
              )}

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          {selectedType && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !message.trim()}
              startIcon={loading && <CircularProgress size={20} />}
            >
              Send Request
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={success}
        autoHideDuration={3000}
        onClose={() => setSuccess(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(false)}>
          Your request has been sent successfully!
        </Alert>
      </Snackbar>
    </>
  );
};